import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaPhone, FaCalendarAlt, FaEnvelope } from 'react-icons/fa';
import publicData from '../../../data/publicData.json';

const SecondaryNavbar = ({ theme, companyInfo }) => {
  const isDark = theme === 'dark';
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const [ctaDropdownOpen, setCtaDropdownOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { name, logos, ctas } = companyInfo;

  const navBgClass = isDark 
    ? 'bg-gradient-to-r from-gray-900 to-black text-white' 
    : 'bg-gradient-to-r from-gray-100 to-white text-gray-900';
  const menuBgClass = isDark 
    ? 'bg-gray-900/95 text-white backdrop-blur-sm' 
    : 'bg-white/95 text-gray-900 backdrop-blur-sm';
  const hoverTextClass = isDark ? 'hover:text-blue-400' : 'hover:text-blue-600';
  const logoTextClass = isDark ? 'text-white' : 'text-gray-900';
  const buttonBgClass = isDark 
    ? 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700' 
    : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800';

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const mobileMenuVariants = {
    hidden: { 
      opacity: 0,
      x: "100%"
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300
      }
    },
    exit: {
      opacity: 0,
      x: "100%",
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300
      }
    }
  };

  // Reset logo error when theme changes
  useEffect(() => {
    setLogoError(false);
  }, [theme]);

  const getLogoUrl = () => {
    if (!logos) return null;
    const logo = theme === 'dark' ? logos.dark : logos.light;
    if (!logo) return null;
    return logo;
  };

  const logoUrl = getLogoUrl();
  
  const renderLogo = () => {
    if (!logoError && logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={name || 'Company Logo'}
          className="h-8 md:h-10 w-auto object-contain"
          onError={(e) => {
            console.error('Logo failed to load:', logoUrl);
            e.target.onerror = null;
            setLogoError(true);
          }}
        />
      );
    }
    
    return (
      <span className={`text-xl md:text-2xl font-bold ${logoTextClass}`}>
        {name || 'CodeITronics'}
      </span>
    );
  };

  const renderCtaButtons = () => {
    if (!ctas) return null;

    return (
      <div className="relative group">
        <button
          onClick={() => setCtaDropdownOpen(!ctaDropdownOpen)}
          className={`${hoverTextClass} focus:outline-none flex items-center`}
        >
          Quick Actions
          <svg className="ml-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        {ctaDropdownOpen && (
          <div className={`${menuBgClass} absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-2 z-20`}>
            {ctas.contact?.text && (
              <a
                href={ctas.contact.link}
                className={`flex items-center px-4 py-2 ${hoverTextClass}`}
              >
                <FaEnvelope className="mr-2" />
                {ctas.contact.text}
              </a>
            )}
            {ctas.call?.text && (
              <a
                href={ctas.call.link}
                className={`flex items-center px-4 py-2 ${hoverTextClass}`}
              >
                <FaPhone className="mr-2" />
                {ctas.call.text}
              </a>
            )}
            {ctas.appointment?.text && (
              <a
                href={ctas.appointment.link}
                className={`flex items-center px-4 py-2 ${hoverTextClass}`}
              >
                <FaCalendarAlt className="mr-2" />
                {ctas.appointment.text}
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.nav 
      className={`${navBgClass} shadow-lg py-4`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Left side - Logo and Hamburger */}
          <div className="flex items-center space-x-4">
            <motion.button
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-200'} md:hidden`}
              onClick={() => setIsMainMenuOpen(!isMainMenuOpen)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Toggle main menu"
            >
              {isMainMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </motion.button>

            <Link to="/" className="flex items-center space-x-2">
              <motion.div whileHover={{ scale: 1.05 }}>
                {renderLogo()}
              </motion.div>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <motion.div whileHover={{ scale: 1.05 }}>
              <Link to="/" className={`${hoverTextClass} font-semibold`}>Home</Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Link to="/about" className={`${hoverTextClass} font-semibold`}>About Us</Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Link to="/blog" className={`${hoverTextClass} font-semibold`}>Blog</Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Link to="/case-studies" className={`${hoverTextClass} font-semibold`}>Case Studies</Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Link to="/services" className={`${hoverTextClass} font-semibold`}>Our Services</Link>
            </motion.div>
          </div>

          {/* Right side - Contact Button */}
          <motion.div 
            className="hidden md:block"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/contact"
              className={`${buttonBgClass} text-white px-6 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95`}
            >
              Contact Us
            </Link>
          </motion.div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMainMenuOpen && (
            <motion.div 
              className={`fixed inset-0 z-50 md:hidden ${menuBgClass}`}
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="container mx-auto px-4 py-6">
                <div className="flex justify-end mb-6">
                  <button
                    onClick={() => setIsMainMenuOpen(false)}
                    className={`p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700`}
                  >
                    <FaTimes size={24} />
                  </button>
                </div>
                <div className="flex flex-col space-y-4">
                  <Link to="/" onClick={() => setIsMainMenuOpen(false)} className={`${hoverTextClass} text-lg font-semibold`}>Home</Link>
                  <Link to="/about" onClick={() => setIsMainMenuOpen(false)} className={`${hoverTextClass} text-lg font-semibold`}>About Us</Link>
                  <Link to="/blog" onClick={() => setIsMainMenuOpen(false)} className={`${hoverTextClass} text-lg font-semibold`}>Blog</Link>
                  <Link to="/case-studies" onClick={() => setIsMainMenuOpen(false)} className={`${hoverTextClass} text-lg font-semibold`}>Case Studies</Link>
                  <Link to="/services" onClick={() => setIsMainMenuOpen(false)} className={`${hoverTextClass} text-lg font-semibold`}>Our Services</Link>
                  <Link
                    to="/contact"
                    onClick={() => setIsMainMenuOpen(false)}
                    className={`${buttonBgClass} text-white px-6 py-3 rounded-lg text-center transition-colors duration-200 mt-4 font-semibold`}
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default SecondaryNavbar; 